import { useMemo, useState } from "react";
import { Disclosure, Transition } from "@headlessui/react";
import {
  ChevronDownIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  SparklesIcon,
  StarIcon as StarIconSolid,
} from "@heroicons/react/24/solid";
import { StarIcon as StarIconOutline } from "@heroicons/react/24/outline";
import {
  Competency,
  CompetencySignal,
  EvidenceConfidence,
  suggestLevel,
} from "../../../api/interviewEvaluation.api";

export interface CompetencyDraft {
  level: number | null;
  notObserved: boolean;
  notObservedReason: string;
  deviationReason: string;
  confidence: EvidenceConfidence;
  evidenceText: string;
  signalIds: string[];
}

type Props = {
  competency: Competency;
  draft: CompetencyDraft;
  readOnly: boolean;
  /** True while this card's evidence still matches an unedited transcript draft. */
  aiDrafted?: boolean;
  /** Omitted in read-only mode, where nothing in this card is interactive anyway. */
  onChange?: (next: CompetencyDraft) => void;
};

const CONFIDENCES: EvidenceConfidence[] = ["Low", "Medium", "High"];

export default function CompetencyScoreCard({
  competency,
  draft,
  readOnly,
  aiDrafted,
  onChange,
}: Props) {
  const positives = useMemo(
    () => competency.signals.filter((s) => s.polarity === "Positive"),
    [competency.signals],
  );
  const concerns = useMemo(
    () => competency.signals.filter((s) => s.polarity === "Concern"),
    [competency.signals],
  );

  const selected = new Set(draft.signalIds);
  const positiveCount = positives.filter((s) => selected.has(s.id)).length;
  const concernCount = concerns.filter((s) => selected.has(s.id)).length;
  const suggested = suggestLevel(positiveCount, concernCount, positives.length);

  const hasEvidenceContent =
    draft.evidenceText.trim().length > 0 || draft.signalIds.length > 0;

  // Hover preview for the star rating - purely local UI state, never persisted. Falls
  // back to whatever's actually selected once the mouse leaves, so the anchor text
  // stays legible without needing to re-hover.
  const [hoverLevel, setHoverLevel] = useState<number | null>(null);
  const displayLevel = hoverLevel ?? (draft.notObserved ? null : draft.level);
  const displayAnchor = displayLevel
    ? competency.levelAnchors.find((a) => a.level === displayLevel)
    : null;

  const toggleSignal = (signal: CompetencySignal) => {
    if (readOnly) return;
    const next = new Set(draft.signalIds);
    next.has(signal.id) ? next.delete(signal.id) : next.add(signal.id);
    onChange?.({ ...draft, signalIds: Array.from(next) });
  };

  // The score is the mandatory input and is never gated on evidence or signals -
  // it is always clickable (unless the card is read-only).
  const setLevel = (level: number) => {
    if (readOnly) return;
    onChange?.({
      ...draft,
      level: draft.level === level ? null : level,
      notObserved: false,
    });
  };

  const toggleNotObserved = () => {
    if (readOnly) return;
    const notObserved = !draft.notObserved;
    onChange?.({
      ...draft,
      notObserved,
      level: notObserved ? null : draft.level,
    });
  };

  const signalRow = (
    signal: CompetencySignal,
    tone: "positive" | "concern",
  ) => {
    const isChecked = selected.has(signal.id);
    return (
      <label
        key={signal.id}
        className={`flex items-start gap-2 py-1 text-sm ${
          readOnly ? "cursor-default" : "cursor-pointer"
        } ${
          isChecked
            ? tone === "positive"
              ? "text-green-800 dark:text-green-300"
              : "text-orange-800 dark:text-orange-300"
            : "text-gray-600 dark:text-gray-400"
        }`}
      >
        <input
          type="checkbox"
          checked={isChecked}
          disabled={readOnly}
          onChange={() => toggleSignal(signal)}
          className={`mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 dark:bg-slate-800 focus:ring-1 ${
            tone === "positive"
              ? "text-green-600 focus:ring-green-500"
              : "text-orange-500 focus:ring-orange-500"
          }`}
        />
        <span>{signal.text}</span>
      </label>
    );
  };

  const evidenceSection = (
    <div className="space-y-4 pt-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
            Observed
          </p>
          {positives.map((s) => signalRow(s, "positive"))}
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
            Concerns
          </p>
          {concerns.map((s) => signalRow(s, "concern"))}
        </div>
      </div>

      {suggested !== null && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Ticked signals suggest <strong>level {suggested}</strong> - purely a
          hint, your score stands regardless.
        </p>
      )}

      <div>
        <div className="flex items-center gap-2 mb-1">
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Evidence (optional) &mdash; what did the candidate do or say?
          </label>
          {aiDrafted && !readOnly && (
            <span className="flex items-center gap-1 rounded-full bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 text-[11px] font-medium text-indigo-700 dark:text-indigo-300">
              <SparklesIcon className="h-3 w-3" />
              AI-drafted &mdash; review
            </span>
          )}
        </div>
        <textarea
          rows={3}
          disabled={readOnly}
          value={draft.evidenceText}
          onChange={(e) =>
            onChange?.({ ...draft, evidenceText: e.target.value })
          }
          placeholder="Optional, but the more specific the better - e.g. 'Restated the requirement before answering, then split it into who triggers it and what happens on failure.'"
          className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-slate-800 dark:text-gray-200 text-sm focus:border-entntblue focus:ring-entntblue disabled:opacity-60"
        />
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Confidence
        </span>
        {CONFIDENCES.map((c) => (
          <button
            key={c}
            type="button"
            disabled={readOnly}
            onClick={() => onChange?.({ ...draft, confidence: c })}
            className={`px-3 py-1 rounded-full text-xs border transition ${
              draft.confidence === c
                ? "bg-gray-800 text-white border-gray-800 dark:bg-gray-200 dark:text-gray-900 dark:border-gray-200"
                : "bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600"
            } ${readOnly ? "opacity-60 cursor-default" : "cursor-pointer"}`}
          >
            {c}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 p-6">
      {/* Header */}
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
          {competency.name}
        </h3>
        <div className="flex items-center gap-2 text-xs">
          {competency.isObservationOnly ? (
            <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              Recorded only &middot; does not affect the score
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded bg-blue-50 text-entntblue dark:bg-blue-900/30 dark:text-blue-300">
              weight {Math.round(competency.weight * 100)}%
            </span>
          )}
          {competency.minimumLevel != null && (
            <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              minimum {competency.minimumLevel}
            </span>
          )}
        </div>
      </div>

      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
        {competency.definition}
      </p>

      {competency.key === "COMMUNICATION" && (
        <div className="mb-5 flex gap-2 rounded-md bg-blue-50 dark:bg-blue-900/20 p-3 text-xs text-blue-900 dark:text-blue-200">
          <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Do <strong>not</strong> score down for accent, grammar, speaking
            speed, quietness, or nervousness early on. Level 3 is &ldquo;not
            polished, entirely followable&rdquo; and is fully acceptable.
          </span>
        </div>
      )}

      {/* ── THE SCORE — the primary, unmissable input on this step ───────────── */}
      <div className="mb-1">
        <p className="text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300 mb-3">
          Your score
        </p>

        <div
          className="flex items-center gap-1.5"
          onMouseLeave={() => setHoverLevel(null)}
        >
          {[1, 2, 3, 4, 5].map((level) => {
            const effectiveLevel = draft.notObserved ? 0 : draft.level ?? 0;
            const filled = level <= (hoverLevel ?? effectiveLevel);
            const StarIcon = filled ? StarIconSolid : StarIconOutline;
            return (
              <button
                key={level}
                type="button"
                disabled={readOnly}
                onMouseEnter={() => !readOnly && setHoverLevel(level)}
                onClick={() => setLevel(level)}
                aria-label={`Score ${level} out of 5`}
                className={`transition-transform duration-100 ${
                  readOnly ? "cursor-default" : "cursor-pointer hover:scale-110"
                }`}
              >
                <StarIcon
                  className={`h-11 w-11 transition-colors ${
                    filled
                      ? "text-amber-400"
                      : "text-gray-300 dark:text-gray-600"
                  }`}
                />
              </button>
            );
          })}

          {draft.level !== null && !draft.notObserved && (
            <span className="ml-2 text-xl font-bold text-gray-700 dark:text-gray-200">
              {draft.level}
              <span className="text-sm font-normal text-gray-400">/5</span>
            </span>
          )}
        </div>

        {/* Anchor text - hidden until you hover a star (or, at rest, shows whichever
            level is currently selected so the choice stays legible without re-hovering). */}
        {displayAnchor && (
          <div className="mt-3 min-h-[3.25rem] rounded-md bg-gray-50 dark:bg-slate-800/60 px-3.5 py-2.5 text-sm text-gray-600 dark:text-gray-300">
            <>
              <span className="font-semibold text-gray-800 dark:text-gray-100">
                Level {displayAnchor.level}:{" "}
              </span>
              {displayAnchor.description}
            </>
          </div>
        )}

        {/* Not observed - visually secondary, deliberately smaller than the score itself */}
        <div className="mt-3 flex items-center gap-2">
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          <span className="text-xs text-gray-400">or</span>
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
        </div>

        <label
          className={`mt-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 ${
            readOnly ? "cursor-default" : "cursor-pointer"
          }`}
        >
          <input
            type="checkbox"
            checked={draft.notObserved}
            disabled={readOnly}
            onChange={toggleNotObserved}
            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 dark:bg-slate-800 text-entntblue focus:ring-entntblue"
          />
          I didn&rsquo;t get to observe this competency
        </label>

        {draft.notObserved && (
          <input
            type="text"
            disabled={readOnly}
            value={draft.notObservedReason}
            onChange={(e) =>
              onChange?.({ ...draft, notObservedReason: e.target.value })
            }
            placeholder="Why was this not observed? e.g. ran out of time before the requirements block"
            className="mt-2 w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-slate-800 dark:text-gray-200 text-sm focus:border-entntblue focus:ring-entntblue"
          />
        )}
      </div>

      {/* ── Optional supporting evidence — visually secondary, collapsed by default ── */}
      <Disclosure defaultOpen={readOnly || aiDrafted || hasEvidenceContent}>
        {({ open }) => (
          <div className="mt-5 pt-5 border-t border-gray-100 dark:border-gray-800">
            <Disclosure.Button
              disabled={readOnly}
              className="flex w-full items-center gap-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:cursor-default"
            >
              <ChevronDownIcon
                className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
              />
              {hasEvidenceContent
                ? "Supporting evidence"
                : "Add supporting evidence (optional)"}
            </Disclosure.Button>
            {!readOnly && (
              <div className="mt-2 flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-900/15 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
                <LightBulbIcon className="h-4 w-4 shrink-0 mt-0.5 text-amber-500" />
                <span>
                  <strong className="font-semibold">Tip:</strong> ticking
                  signals or noting what you observed can help suggest a score -
                  your own judgement always comes first.
                </span>
              </div>
            )}

            <Transition
              show={open}
              enter="transition duration-150 ease-out"
              enterFrom="transform opacity-0 -translate-y-1"
              enterTo="transform opacity-100 translate-y-0"
              leave="transition duration-100 ease-in"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Disclosure.Panel static>{evidenceSection}</Disclosure.Panel>
            </Transition>
          </div>
        )}
      </Disclosure>
    </div>
  );
}
